import type { CrusherS3Plan } from 'api-validations/crusher'
import { PlanAsset } from 'api-validations/locutus'
import type { MeasureSystem } from 'api-validations/shared'
import { ApplicationType, type ControllableApplication } from 'api-validations/stitch'
import { type Cff, sameCff } from 'shared-ui/operation-wide-data'

type BoundaryOutline = Extract<CrusherS3Plan, { type: 'boundary' }>['data']['boundary']
type RateZones = Extract<CrusherS3Plan, { type: 'rx' }>['data']['prescription']
type RateZoneProperties = NonNullable<RateZones['features'][number]['properties']>

/**
 * A field already known to the operation.
 */
export type PlanningField = Cff & { id: string }

/**
 * The asset types an upload can hold.
 */
export type UploadAssetType = PlanAsset.Boundary | PlanAsset.Prescription

/**
 * One boundary or prescription found in an upload. `id` is stable across re-reads, so the grower's
 * edits survive them.
 */
export type ParsedPlanEntry = {
  id: string
  sourceId: string
  assetId: string
  /**
   * The asset's name from the source.
   */
  sourceName: string
  /**
   * The field the asset was exported under (if the source records one).
   */
  cff?: Cff
  /**
   * Whether the source states the asset's type (.2020) or leaves it to the grower (shapefile).
   */
  assetTypeIsSettled: boolean
} & (
  | { assetType: PlanAsset.Boundary; boundary: BoundaryOutline; inverted: boolean }
  | {
      assetType: PlanAsset.Prescription
      prescription: RateZones
      applicationType: ControllableApplication
      rateColumn?: string
    }
)

/**
 * How crusher is to read a shapefile: its asset type and rate units.
 */
export type PlanFileReading =
  | { assetType: PlanAsset.Boundary }
  | {
      assetType: PlanAsset.Prescription
      applicationType: ControllableApplication
      measureSystem: MeasureSystem
    }

export const DEFAULT_APPLICATION_TYPE = ApplicationType.Seeding

export enum CffTargetStatus {
  Unassigned = 'unassigned',
  Existing = 'existing',
  /**
   * Named by the upload but missing from the operation; created on import.
   */
  New = 'new'
}

export type CffTarget =
  | { status: CffTargetStatus.Unassigned }
  | { status: CffTargetStatus.Existing; fieldId: string; cff: Cff }
  | { status: CffTargetStatus.New; cff: Cff }

export const UNASSIGNED_CFF: CffTarget = { status: CffTargetStatus.Unassigned }

/**
 * Resolves a name tuple to an existing field, or else to a new one. A tuple with any blank name
 * stays unassigned.
 */
export const resolveCffTarget = ({
  cff,
  fields
}: {
  cff: Cff | undefined
  fields: readonly PlanningField[]
}): CffTarget => {
  if (cff === undefined) return UNASSIGNED_CFF
  if ([cff.client, cff.farm, cff.field].some((name) => name.trim() === '')) return UNASSIGNED_CFF

  const existing = fields.find((field) => sameCff(field, cff))
  return existing === undefined
    ? { status: CffTargetStatus.New, cff }
    : { status: CffTargetStatus.Existing, fieldId: existing.id, cff }
}

export const BLANK_CFF: Cff = { client: '', farm: '', field: '' }

const ZONE_LABELS = new Set(['zone', 'name'])

export const rateColumns = (prescription: RateZones): readonly string[] => [
  ...new Set(
    prescription.features.flatMap((feature) =>
      Object.keys(feature.properties ?? {}).filter((key) => !ZONE_LABELS.has(key))
    )
  )
]

/**
 * Whether a prescription's zones carry any rate. A shapefile without its .dbf still converts, into
 * zones with no properties.
 */
export const hasRates = (entry: ParsedPlanEntry) =>
  entry.assetType !== PlanAsset.Prescription || rateColumns(entry.prescription).length > 0

export const withRateColumn = ({
  prescription,
  column
}: {
  prescription: RateZones
  column: string
}): RateZones => ({
  ...prescription,
  features: prescription.features.map((feature) => ({
    ...feature,
    properties:
      feature.properties === null
        ? null
        : (Object.fromEntries(
            Object.entries(feature.properties).filter(
              ([key]) => ZONE_LABELS.has(key) || key === column
            )
          ) as RateZoneProperties)
  }))
})
